import { QueryClient } from "@tanstack/react-query"
import { Meeting, ResourceType } from "../types"


export const patchQueryData = (
    queryClient: QueryClient,
    data: any,
    type: string,
    idName: string,
    invalidateSubTypes: string[] = [],
    validationFunction?: (item: any) => boolean
) => {
    if (!data) {
        console.warn("Tried to update queries without data")
        return
    }

    const itemId = data[idName]

    if (!itemId) {
        console.warn("Tried to update queries with invalid item")
        return
    }

    const queries = queryClient.getQueryCache().findAll({
        predicate: (query) => {
            const queryKey = query.queryKey as unknown as (string | undefined)[]

            return Array.isArray(queryKey) &&
                queryKey[0] === type &&
                !queryKey.some(
                    (subKey) => subKey && invalidateSubTypes.includes(subKey)
                )
        }
    })

    queries.forEach((query) => {
        const prev = queryClient.getQueryData(query.queryKey) as any

        if (Array.isArray(prev)) {
            const updated = prev.map((item) =>
                item &&
                    item[idName] === itemId &&
                    (!validationFunction || validationFunction(item))
                    ? { ...item, ...data }
                    : item
            )

            queryClient.setQueryData(query.queryKey, updated)
        } else if (
            prev &&
            prev[idName] === itemId &&
            (!validationFunction || validationFunction(prev))
        ) {
            queryClient.setQueryData(query.queryKey, {
                ...prev,
                ...data
            })
        }
    })

    for (const invalid of invalidateSubTypes) {
        queryClient.invalidateQueries({
            queryKey: [type, itemId, invalid]
        })
    }
}

export const updateQueryData = (
    queryClient: QueryClient,
    data: any,
    type: string,
    idName: string,
    invalidateSubTypes: string[] = []
) => {

    if (!data) {
        console.warn("Tried to update queries without data")
        return
    }

    const itemId = data[idName]

    if (!itemId) {
        console.warn("Tried to update queries with invalid item")
        return
    }

    const queries = queryClient.getQueryCache().findAll({
        predicate: (query) => {
            const queryKey = query.queryKey as unknown as (string | undefined)[]

            if (!Array.isArray(queryKey)) {
                return false
            }

            for (let subKey of queryKey) {
                if (subKey && invalidateSubTypes.includes(subKey)) {
                    console.log("Ignoring", queryKey)
                    return false
                }
            }

            return (
                queryKey[0] === type
            )
        }
    })

    queries.forEach((query) => {
        const prev = queryClient.getQueryData(query.queryKey) as any

        if (Array.isArray(prev)) {
            let updatedItem = false
            // It's a list of items
            const updated = prev.map((m) => {
                updatedItem = true
                return m && m[idName] === itemId ? data : m
            })

            if (updatedItem) {
                queryClient.setQueryData(query.queryKey, updated)
            }

        } else if (prev && prev[idName] == itemId) {
            // It's a single item
            queryClient.setQueryData(query.queryKey, data)
        }
    })

    for (let invalid of invalidateSubTypes) {
        queryClient.invalidateQueries({ queryKey: [type, itemId, invalid] })
    }
}

export const addQueryDataToList = (
    queryClient: QueryClient,
    data: any,
    queryKey: unknown[]
) => {
    if (!data) {
        return
    }

    const query = queryClient.getQueryData(queryKey) as any

    if (!query) {
        return
    }

    if (!Array.isArray(query)) {
        return
    }

    const newData = [...query, data]

    queryClient.setQueryData(queryKey, newData)
}


export const addMeetingToListMeetingQueries = (queryClient: QueryClient, meeting: Meeting | null) => {
    if (!meeting || !meeting.meetingId) {
        console.log("No meeting to add")
        return
    }

    const queries = queryClient.getQueryCache().findAll({
        predicate: (query) => {
            const key = query.queryKey;
            // Match [ResourceType.Meeting, startRange, endRange, clientId]
            return Array.isArray(key) && key[0] === ResourceType.Meeting && key.length === 4;
        }
    });

    queries.forEach((query) => {
        const [_, __, startRange, endRange] = query.queryKey;

        // Only use startRange/endRange if they are valid date strings or numbers
        const start = (typeof startRange === "string" || typeof startRange === "number")
            ? new Date(startRange)
            : null;
        const end = (typeof endRange === "string" || typeof endRange === "number")
            ? new Date(endRange)
            : null;

        // Check if meeting is in range
        const inRange =
            (!start || meeting.startTime >= start) &&
            (!end || meeting.startTime <= end);

        if (inRange) {
            const prev = queryClient.getQueryData(query.queryKey) as Meeting[] | [];

            const newData = [...prev, meeting]

            queryClient.setQueryData(query.queryKey, newData);
        }
    });
}